import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { next, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { glueDimensionValues, isAlive } from 'webapp/utils/common';

import AnnotationFormComponent from 'webapp/components/annotation/form';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import MetricSetup from 'webapp/models-custom/metric-setup';

interface MetricChoice extends LabelValuePairString {
	dataItem: MetricSelectionData;
}

export default class AnnotationPopupEditComponent extends PopupInnerContextComponent<PopupData.Annotation.Edit> {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;

	@tracked submitInProgress = false;

	get model() {
		return this.popupData.model;
	}

	@cached
	get metricChoices(): MetricChoice[] {
		const metricChoices: MetricChoice[] = [];
		this.popupData.metricSelectionData.forEach((metricSelectionDataItem) => {
			const metricSetupData = metricSelectionDataItem.metricSetupData;
			const uniqueStr = `${metricSetupData?.key}-${metricSetupData?.spaceAccessId}-${metricSetupData?.dimensions?.[0]}-${Object.entries(metricSelectionDataItem.dimensionValues).join(':')}-${metricSelectionDataItem.unit}`;
			if (!metricChoices.find((item) => item.value === uniqueStr)) {
				let label = metricSelectionDataItem.metricName || '';
				if (Object.keys(metricSelectionDataItem.dimensionValues).length) {
					label += ` / ${glueDimensionValues(metricSelectionDataItem.dimensionValues)}`;
				}
				if (metricSelectionDataItem.unit) {
					label += ` / ${metricSelectionDataItem.unit}`;
				}
				metricChoices.push({
					value: uniqueStr,
					label,
					dataItem: metricSelectionDataItem,
				});
			}
		});
		return metricChoices;
	}

	get title() {
		return (this.model.isNew ? 'Add' : 'Edit') + ' Annotation';
	}

	get showMetricChoice() {
		return this.model.isNew && this.model.singleMetricOnly && this.metricChoices.length > 1;
	}

	get isCheckboxEditDisabled() {
		return this.submitInProgress || !this.model.isNew;
	}

	@action
	handleSingleMetricOnlyChange() {
		if (this.showMetricChoice) {
			scheduleOnce('afterRender', this, this.reposition);
		}
	}

	@action
	changeMetric(uniqueId: string) {
		const metricSelectionDataItem = this.metricChoices.find(
			(item) => item.value === uniqueId,
		)?.dataItem;
		if (metricSelectionDataItem) {
			const metricSetup = new MetricSetup(
				metricSelectionDataItem.metricSetupData || undefined,
			);

			metricSetup.setFiltersFromDimensionsAndValuesMapAndUnit(
				metricSelectionDataItem.dimensionValues,
				metricSelectionDataItem.unit,
			);

			if (metricSetup.metricSetupData) {
				this.model.metricSetup.setFromMetricSetupData(metricSetup.metricSetupData);
			}
		}

		scheduleOnce('afterRender', this, this.reposition);
	}

	@action
	save() {
		if (this.accountService.handleDemo()) {
			return;
		}

		const annotation = this.model;

		if (!annotation.note) {
			if (annotation.isNew) {
				this.close();
			} else {
				// delete note if we've deleted the text
				this.delete();
			}
		} else {
			this.submitInProgress = true;

			annotation
				.save()
				.then(
					() => {
						this.popupData.onUpdate(annotation);
						next(() => {
							this.close();
						});
					},
					(errorData: unknown) => {
						throw errorData;
					},
				)
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	@action
	delete() {
		const annotation = this.model;

		this.submitInProgress = true;

		annotation
			.delete()
			.then(() => {
				if (this.popupData.onDelete) {
					this.popupData.onDelete(annotation);
				}

				next(() => {
					if (isAlive(this)) {
						this.close();
					}
				});
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="row">
			<div class="title">{{this.title}}</div>
			<a role="button" class="close" {{on "click" this.close}}>
				<CommonUiIconComponent @name="Close" @size="sm" />
			</a>
		</div>
		<AnnotationFormComponent
			@model={{this.model}}
			@submitInProgress={{this.submitInProgress}}
			@onSave={{this.save}}
			@onCancel={{this.close}}
			@onDelete={{unless this.model.isNew this.delete}}
			@onChangeMetric={{this.changeMetric}}
			@onSingleMetricOnlyChange={{this.handleSingleMetricOnlyChange}}
			@showMetricChoice={{this.showMetricChoice}}
			@metricChoices={{this.metricChoices}}
			@isCheckboxEditDisabled={{this.isCheckboxEditDisabled}}
		/>
	</template>
}
